import React, { useCallback } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Staff } from "../../types/Staff";
import useStaffService from "../../services/administration/useStaffService";
import useToast, { ToastType } from "../../hooks/useToast";
import { useDialogForm } from "../../hooks/useDialogForm";

interface ChangePasswordDialogProps {
    open: boolean;
    staffMember?: Staff;
    onClose: () => void;
}

type ChangePasswordFields = {
    newPassword: string;
};

const DEFAULT_FIELDS: ChangePasswordFields = {
    newPassword: "",
};

const REQUIRED_FIELDS = [
    'newPassword',
] as const satisfies ReadonlyArray<keyof ChangePasswordFields>;

function ChangePasswordDialog({ open, staffMember, onClose }: ChangePasswordDialogProps) {
    const { changeStaffPassword } = useStaffService();
    const { showToast } = useToast();
    const { t } = useTranslation("common");
    const {
        fields,
        handleChange,
        validateForm,
        setFieldTouched,
        shouldShowFieldError,
    } = useDialogForm<ChangePasswordFields>({
        config: {
            requiredFields: REQUIRED_FIELDS,
            defaultValues: DEFAULT_FIELDS,
            additionalValidation: (currentFields) => currentFields.newPassword.trim().length > 0,
        },
        isOpen: open,
    });

    const handlePasswordChange = useCallback(async () => {
        if (!staffMember) {
            return;
        }

        if (!validateForm()) {
            return;
        }

        try {
            await changeStaffPassword(staffMember.id, fields.newPassword);
            showToast({
                message: t("administration.locationAdministration.passwordChangedSuccessfully", { name: staffMember.name }),
                type: ToastType.SUCCESS,
            });
            onClose();
        } catch (error) {
            showToast({
                message: t("common.unknownError"),
                type: ToastType.ERROR,
            });
        }
    }, [staffMember, validateForm, changeStaffPassword, fields.newPassword, showToast, t, onClose]);

    const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handlePasswordChange();
        }
    }, [handlePasswordChange]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>
                {t("common.changePassword")}
            </DialogTitle>
            <DialogContent>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    {t("administration.locationAdministration.changePasswordFor", { name: staffMember?.name })}
                </Typography>
                <TextField
                    autoFocus
                    margin="dense"
                    label={t("common.newPassword")}
                    type="password"
                    fullWidth
                    variant="outlined"
                    value={fields.newPassword}
                    onChange={(e) => handleChange('newPassword', e.target.value)}
                    onBlur={() => setFieldTouched('newPassword')}
                    error={shouldShowFieldError('newPassword')}
                    helperText={shouldShowFieldError('newPassword') ? t("common.pleaseEnterPassword") : ' '}
                    onKeyDown={handleKeyPress}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>
                    {t("common.cancel")}
                </Button>
                <Button onClick={handlePasswordChange} variant="outlined">
                    {t("common.save")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default ChangePasswordDialog;
